import type { AccountStatusType } from "../enum/account-status-types";
import type { ClientActivationCodeType } from "../enum/client-activation-code-types";
import type { JumioStatus } from "../enum/jumio-status";
import type { LimitPeriods } from "../enum/limit-types-periods";
import type { PlayerPromotionTypes } from "../enum/promotion-types";
import type { ServerMessageCodes } from "../enum/server-message-codes";
import type { StatusReasonIDs } from "../enum/status-reason-ids";
import type { Currency } from "./currency-type";
import type { Game, GameInfoValue } from "./game";
import type { LangLabel } from "./lang-label-types";

const ActionTypes = [
    'UserLogin',
    'UserLogout',
    'GetKeyValueTable',
    'AccountActivation',
    'ForgottenPassword',
    'PasswordReset',
    'UserRegistration',
    'JackpotInfo',
    'CheckUsername',
    'KeepAlive',
    'UserDataManipulation',
    'GetPlayerInfo',
    'GameLog',
    'PreviewGame',
    'ConfirmPayout',
    'PaymentTransactionList',
    'StartNewGame',
    'StartOneStopShopGame',
    'PlaysonStartGame',
    'StartPragmaticGame',
    'StartSkyGame',
    'StartSwinttGame',
    'StartCTIGame',
    'StartAmusnetGame',
    'BonusMultiplierWheel',
    'UserPromotionActivation',
    'StartBoomingGame',
    'StartRRGame',
    'StartSswGame',
    'StartFBMDSGame',
    'InitAndExecutePayIn',
    'StartSpnV2Game',
    'StartSpnV2FreeGame',
] as const;

export type ActionType = typeof ActionTypes[number];
export type CasinoRoute = ActionType | 'StartGame';

export type JackpotItemCasinoResponse = {
    dev: number,
    freez: number,
    fro: boolean,
    lvl: number | string,
    ovr: number,
    val: number,
};

export type ClientMessage = {
    code: ServerMessageCodes,
    forMsgType: string,
    infoType: string,
    text: string | number,
};

export type ClientMessageResponse = {
    hasError: boolean,
    hasFailure: boolean,
    message: ClientMessage[],
}

export interface CasinoServiceRequest {
    actionType: ActionType,
    lang: string,
    nid: number,
    prodId: number,
    ip: string,
    curr: number | null,
    uToken: string,
    messages: {
        message: CasinoRequestMessage[],
    },
    turnstileToken?: {textContent: string},
}

export interface CasinoServiceResponse {
    IP: string,
    actionType: ActionType,
    bonusPointsBalance: number,
    cCode: string,
    clientMessage: ClientMessageResponse,
    creditBalance: number,
    curr: number,
    isProxyRequest: boolean,
    jackpotList?: {
        jackpot?: {
            jp?: JackpotItemCasinoResponse[],
        }
    },
    lang: string,
    messages: {
        message: CasinoRequestMessage[],
    },
    nid: number,
    prodId: number,
    uToken: string | number,
    userId: string | number,
    userFlagStr?: string | number,
};

export type ForgottenPasswordParams = {
    email?: string,
    phone?: string,
    username?: string,
};

export type PasswordResetParams = {
    newPassword: string,
    changeCode: string,
    verificationType: ClientActivationCodeType,
    username?: string,
};

export type UserLoginRequestParams = {
    uid: string,
    pwd: string,
    loginData: string,
    os: string,
    dType: string,
    qs: string,
    campaign?: string,
    deviceId?: string,
    fcmToken?: string,
};

export type AccountActivationRequestParams = {
    codeType: string,
    code: string | number,
    loginData: string,
    os: string,
    dType: string,
    qs: string,
    username?: string,
};

export type CheckPlayersDataParams = {
    username: string,
    alreadyExists?: boolean,
    hasError?: boolean,
};

export type CountryTableItem = {
    key: number,
    value: string,
    cCode: string,
    pCode: number,
    sel: boolean,
    age: number,
};

export type PersonalSettingsRoot = {
    favoriteGames?: string | {textContent: string},
    lang?: string | {textContent: string},
    soundEnabled?: boolean,
    paysafeToken?: string,
};

export type PlayerInfoResponse = {
    firstCall?: boolean,
    brokenGame?: {
        load?: boolean,
        hasBrokenGame?: boolean,
        instanceId?: number,
    },
    termsAcceptance?: {
        termsAccepted?: boolean,
        privacyAccepted?: boolean,
        load?: boolean
    },
    favorites?: {
        load?: boolean,
        favoriteGames?: number | string,
    },
    playerUI?: {
        load?: boolean,
        uiType?: number
    },
    personalSettings?: {
        load?: boolean,
        root?: PersonalSettingsRoot,
    },
    withdraw?: {
        load?: boolean,
        paymentTransactionId?: number,
        paymentProviderId?: number
    },
    deposit?: {
        load?: boolean,
        paymentProviderId?: number
    },
    promotionsStatus?: {
        load?: boolean
    },
    paymentMethods?: {
        load?: boolean,
        hasPayCard?: boolean,
        status?: number,
        deposits?: number,
        method?: {
            id?: number,
            type?: string,
            active?: boolean,
            withdraw?: boolean,
            deposit?: boolean,
            depositMin?: number,
            depositMax?: number,
            withdrawMin?: number,
            withdrawMax?: number,
        }[],
    },
    playerPaymentInfo?: {
        load?: boolean,
        paymentInfo?: {
            playerPaymentInfoId?: number,
            providerId?: number,
            email?: number | string,
            useForEasyPay?: boolean,
            useForWithdraw?: boolean,
            disabled?: boolean,
            cardMasked?: number | string,
        }[],
    },
    mainAccount?: {
        load?: boolean,
        hasX1Token?: boolean,
        minus?: number,
        convertBonusPoints?: boolean,
        amountToConvert?: number,
        forUserId?: number,
        accountingCreditsRate?: number,
        gameCreditsRate?: number,
        credit?: {
            type?: MainAccountCreditTypes,
            amount?: number,
            label?: string,
        }[],
    },
    sportBetting?: {
        load?: boolean,
    },
    promotions?: {
        load?: boolean,
        asLogin?: boolean,
        playerPromotion?: {
            id?: number,
            gameInstanceId?: number,
            activated?: boolean,
            used?: boolean,
            type?: PlayerPromotionTypes,
            freeGames?: number,
            freeGamesLeft?: number,
            level?: number,
            fgLevel?: number,
            freeGameWin?: number,
            freePlayAmount?: number,
            freePlayAmountLeft?: number,
            freePlayWin?: number,
            userPromotionId?: number, 
            notificationTypeId?: number,
            validFrom?: string, 
            validTo?: string,
            transactionScopeId?: string,
            depositAmount?: number,
            expiresInDays?: number,
            repetitionNumber?: number,
            minDepositCount?: number,
            maxDepositCount?: number,
            code?: string,
            applyOnId?: number,
            gameDesc?: string,
            wheel_segments?: string,
        }[],
    },
};

export type KeepAliveResponse = {
    doRequest: boolean,
    action: string,
    getStatus?: boolean,
    prolongSession?: boolean,
    bonusPoints?: number,
    cVal?: number,
    actions?: {
        name: 'GetPlayerInfo' | 'PaymentTransactionList' | 'UserDataManipulation',
    },
    externalActions?: {
        provider?: string,
        transactionId?: string,
    },
};

export type Limit = {
    id?: number,
    period?: LimitPeriods,
    type?: number,
    amount?: string,
    start?: string,
    end?: string,
};

export type UserMessageResponse = {
    currency?: Currency,
    sportBettingLoginSuccess?: boolean,
    sportBettingBalance?: number,
    loadUserId?: number,
    userName?: string,
    email?: string,
    timeCreated?: string,
    lastName?: string,
    firstName?: string,
    category?: string,
    gender?: string,
    birthDate?: string,
    phone?: string,
    address1?: string,
    state?: string,
    recommendationByACK?: {
        'xmlns:p3': string,
        'p3:nil': boolean
    },
    city?: string,
    postalCode?: string | number,
    countryId?: number,
    country?: string,
    sendNotificationsToEmail?: boolean,
    VATStatus?: boolean,
    timeZone?: number,
    timeZoneOffset?: number,
    registrationPromotionAmount?: number,
    payVisionCardId?: {
        'xmlns:p3': string,
        'p3:nil': boolean
    },
    useForEasyPay?: boolean,
    useForWithdraw?: boolean,
    ecoPayzAccountNumber?: {},
    statusReasonId?: StatusReasonIDs,
    statusId?: AccountStatusType,
    documents?: {
        save: boolean
    },
    userDocumentTypeId?: number,
    partnerId?: string,
    partnerLobbyUrl?: string,
    productId?: number,
    limits?: {
        now?: string,
        limit?: Limit[],
    },
    payCardTreshold?: number,
    excludedFromMarketing?: boolean,
    acceptEmail?: boolean,
    acceptSMS?: boolean,
    acceptCall?: boolean,
    acceptNotifications?: boolean,
    privacyAccepted?: {
        'xmlns:p3': string,
        'p3:nil': boolean
    },
    privacyVersion?: string,
    termsAccepted?: {
        'xmlns:p3': string,
        'p3:nil': boolean
    },
    termsVersion?: string,
    userId?: number | string,
    formatedUserId?: number,
    balance?: number,
    personalSettings?: {
        root?: PersonalSettingsRoot,
    },
    securityNumber?: string,
    preferedLanguageId?: number,
    bankName?: string,
    accountNo?: string,
    SWIFT?: string,
    IBANCode?: string,
    statusValidDate?: string,
    jumioStatusId?: JumioStatus,
    jumioRedirectUrl?: string,
    devices?: {
        device?: {
            deviceId: string,
            installationId: string,
        }[],
    },
    referral?: {
        promoId: number,
        maxFriends: number,
        activeFriends: number,
        totalPrice: number,
        validTo: string,
        rewardAmount: number,
        minDepositNeeded: number,
    },
};

export type GameHistoryLog = {
    id: number,
    date: string,
    bet: number,
    win: number,
    game: string,
    giId: number,
    gsId: number,
    actualGame?: Game,
    disabled?: boolean,
};

export type UserPromotionActivationMessageResponse = {
    success: boolean,
    promotionId: number,
    gameId: GameInfoValue,
    fgLevel: number,
    wheel_segments?: string,
};

export type UserPromotionActivationMessage = {
    request?: {
        activatePromoType: 'One',
        userId: string,
        promotionCode: string,
    },
    response?: UserPromotionActivationMessageResponse,
}

export type StartSkyGameRequest = {
    playmode: 'real' | 'fun',
    gameId?: GameInfoValue,
    category?: GameInfoValue,
};

export type StartOneStopShopGameRequest = {
    game: GameInfoValue,
    mobile: boolean,
    freeGames: boolean,
    cat?: GameInfoValue,
};

export type CasinoRequestMessage = {
    type?: string,
    userLogin?: UserLoginRequestParams,
    accountActivation?: AccountActivationRequestParams,
    forgottenPassword?: ForgottenPasswordParams,
    passwordReset?: PasswordResetParams,
    CheckPlayersData?: CheckPlayersDataParams,
    checkPlayersData?: CheckPlayersDataParams,
    UserPromotionActivationMessage?: UserPromotionActivationMessage,
    userPromotionActivationMessage?: UserPromotionActivationMessage,
    notification?: {
        infoContentId: number,
        parameters?: string | number,
    },
    promotionWheel?: {
        promotionId: number,
        deactivate: boolean,
        amount?: number,
        game?: GameInfoValue,
        freeSpins?: number,
        deactivated?: boolean,
        price?: string,
    },
    table?: {
        request?: {
            TableType?: string,
            tableType?: string,
        },
        response?: {
            values: {
                keyVal: CountryTableItem[],
            },
        },
    },
    userMessage?: {
        type?: string,
        request?: UserMessageRequest,
        response?: UserMessageResponse,
    },
    playerInfo?: PlayerInfoResponse,
    keepAlive?: KeepAliveResponse,
    userLogout?: {
        uToken: string,
        sessionEndTypeId: LogoutTypes,
        message?: string,
    },
    gameLog?: {
        request?: GameLogRequestParams,
        response?: {
            pages: number,
            pageSize: number,
            logs?: {
                log?: GameHistoryLog[],
            },
        },
    },
    payin?: {
        ppId: number,
        type: 'StartProcess',
        paymentInfoId?: number,
        resolveType?: 'Standard',
        paymentSuccess?: boolean,
        request?: {
            amount: number,
            actionUser: number,
        },
    },
    previewGame?: {
        gsid?: number,
        gameLogId?: number,
        exGsid?: number,
        subGames?: {
            extgsid?: number,
            exturl?: string,
        },
    },
    PayoutConfirmationMessage?: PayoutConfirmationMessageParams,
    paymentList?: {
        request?: PaymentListRequestParams,
        response?: {
            pages: number,
            pageSize: number,
            transactions?: {
                trans: PaymentTransactionListItem[],
            },
        },
    },
    startNewGame?: {
        ExternalGameId: GameInfoValue,
    },
    startOneStopShopGame?: {
        request: StartOneStopShopGameRequest,
        response?: {
            url: string,
        },
    },
    startPlaysonGame?: {
        request: {
            platform: 'desk' | 'mob',
            exitUrl: string,
            game: GameInfoValue,
            mode: 'real' | 'demo',
        },
        response?: {
            success: boolean,
            gameUrl: string,
        },
    },
    startPragGame?: {
        request: {
            gameId: GameInfoValue,
            isDemo: boolean,
            userId?: string,
        },
        response?: {
            gameUrl?: string,
            error: number,
            description: string,
        },
    },
    startSpnV2Game?: {
        request: {
            gameId: GameInfoValue,
            platform?: 'desktop' | 'mobile',
        },
        response?: {
            url: string,
        },
    },
    startSkyGame?: {
        request: StartSkyGameRequest,
        response?: {
            url: string,
        },
    },
    startswinttGame?: {
        request: {
            gameId: GameInfoValue,
            platform: 'desktop' | 'mobile',
            funMode: boolean,
        },
        response?: {
            url: string,
        },
    },
    startCTIGame?: {
        request: {
            gameId: GameInfoValue,
            playmode: 'Demo' | 'Real',
        },
        response?: {
            url: string,
        },
    },
    startAmusnetGame?: {
        request: {
            gameId: GameInfoValue,
            platform: 'desktop' | 'mobile',
            playmode: 'Real' | 'Demo',
        },
        response?: {
            url: string,
        },
    },
    startBoomingGame?: {
        request: {
            gameId: GameInfoValue,
            platform: 'desktop' | 'mobile',
            playmode: 'Real' | 'Demo',
            lobbyURL: string,
        },
        response?: {
            url: string,
        },
    },
    startRRGame?: {
        request: {
            gameId: GameInfoValue,
            platform: 'desktop' | 'mobile',
            playmode: 'Real' | 'Demo',
            lobby: string,
        },
        response?: {
            url: string,
        },
    },
    startSswGame?: {
        request: {
            gameId: GameInfoValue,
            platform: 'desktop' | 'mobile',
            playmode: 'Real' | 'Demo',
            lobbyURL: string,
            depositURL: string,
        },
        response?: {
            url: string,
        },
    },
    startFBMDSGame?: {
        request: {
            gameId: GameInfoValue,
            playmode: 'Real' | 'Demo',
            platform: 'desktop' | 'mobile',
            lobbyUrl: string,
        },
        response?: {
            url: string,
            token?: string,
        },
    },
}

export type PaymentListRequestParams = {
    date?: string,
    listLength: number,
};

export type CasinoRequestOptions = {
    global?: boolean,
    alwaysReturnAction?: boolean,
    uToken?: string,
    customRoute?: CasinoRoute,
};

export type ClientMessageContent = {
    title?: LangLabel,
    text?: LangLabel,
}

export type UserMessageRequest = {
    nif?: string,
    Gender?: {textContent?: string},
    Country?: {textContent?: string},
    FirstName?: {textContent?: string},
    LastName?: {textContent?: string},
    currency?: {textContent?: string},
    BirthDate?: {textContent?: string},
    password?: {textContent?: string} | string,
    UserName?: {textContent?: string},
    userName?: string,
    Address1?: {textContent?: string},
    City?: {textContent?: string},
    Phone?: {textContent?: string},
    Email?: {textContent?: string},
    dType?: {textContent?: string},
    PostalCode?: {textContent?: string | number},
    PromotionalCode?: {textContent?: string},
    QS?: {textContent?: string},
    btag?: {textContent?: string},
    clickId?: {textContent?: string},
    token?: {textContent?: string},
    deviceId?: {textContent?: string},
    fcmToken?: {textContent?: string},
    RecommendationCode?: {textContent?: string},
    personalSettings?: {
        root?: PersonalSettingsRoot,
    },
    AcceptEmail?: {textContent?: boolean},
    AcceptSMS?: {textContent?: boolean},
    AcceptCall?: {textContent?: boolean},
    AcceptNotifications?: {textContent?: boolean},
    SendNotificationsToEmail?: {textContent?: boolean},
    VATStatus?: {textContent?: boolean},
    CountryId?: {textContent?: number},
    PreferedLanguageId?: {textContent?: number},
    ExcludedFromMarketing?: {textContent?: boolean},
    State?: {textContent?: string},
    BankName?: {textContent?: string},
    AccountNo?: {textContent?: string},
    SWIF?: {textContent?: string},
    IBANCode?: {textContent?: string},
    oldPassword?: {textContent?: string},
    successURL?: string,
    failureURL?: string,
    limits?: {
        limit?: Limit[],
    },
    turnstileToken?: {textContent?: string},
};

export enum LogoutTypes {
    SessionTimeout = 1,
    UserLogout = 2,	
    NewLogin = 3,
    LogoutByBOSUser = 4,
    SessionTimeoutLimit = 5,
    Other = 6,
    Error = 7,
};

export type GameLogRequestParams = {
    listLength?: number,
    gameId?: number,
    date?: string,
};

export type MainAccountCreditTypes = 'CashableCredits' | 'BonusCredits' | 'bonusCredits_MRF' | 'BonusPoints' | 'GameCredits' | 'Money' | 'WithdrawRequest' | 'WithdrawRequestBonus' | 'FreeBets' | 'BetInAction';

export type PayoutConfirmationMessageParams = {
    id: number,
    paymentTranId: number,
    ppId: number,
    paymentProviderId: number,
    type: string,
};

export type ConfirmPayoutCancelProcessParams = {
    paymentTransactionId: number,
    paymentProviderId: number,
};

export type PaymentTransactionListItemType = 'Deposit' | 'Withdraw';

export type PaymentTransactionListItem = {
    ppId: number,
    dateStart: string,
    amount: string,
    bonusAmount: number,
    message: string,
    type: PaymentTransactionListItemType,
    status: string,
    transaction: string | number,
    provider: string,
    id: string | number,
    cancelable: boolean,
    info: string,
};